import type { DeviceLocationStatus } from '@signals/cop-ui/services/copApi';
import { ymdInTimeZoneToUtcInterval } from '../utils/time';

/** Multi-select list semantics for COP device filters (shared with dashboard header). */
export type COPFilterListMode = 'include' | 'exclude';

export function matchesSelectedList(actual: string, selected: string[], mode: COPFilterListMode): boolean {
  if (selected.length === 0) return true;
  const hit = selected.includes(actual);
  return mode === 'exclude' ? !hit : hit;
}

export type COPDeviceFilterSnapshot = {
  selectedCourses: string[];
  courseFilterMode: COPFilterListMode;
  selectedDeviceTypes: string[];
  deviceTypeFilterMode: COPFilterListMode;
  selectedEntityTypes: string[];
  entityTypeFilterMode: COPFilterListMode;
  selectedEntities: string[];
  entityFilterMode: COPFilterListMode;
};

/** RP/SRP often live under a different course (e.g. Exercise Support). */
export function isRpOrSrpEntityType(entityType: string | undefined | null): boolean {
  return entityType === 'RP' || entityType === 'SRP';
}

export const EXERCISE_SUPPORT_COURSE_ID = 'Exercise Support';

/**
 * Explicitly selected RP/SRP IDs may be shown as COP companions even when their
 * course (or entity-type filter) would otherwise exclude them — e.g. itinerary
 * subject on a training course plus RP/SRP from Exercise Support.
 */
function isExplicitRpSrpCompanion(
  d: Pick<DeviceLocationStatus, 'entity_type' | 'entity_id'>,
  f: COPDeviceFilterSnapshot
): boolean {
  if (f.selectedEntities.length === 0 || f.entityFilterMode !== 'include') return false;
  if (!isRpOrSrpEntityType(d.entity_type)) return false;
  return f.selectedEntities.includes(d.entity_id || '');
}

/**
 * When entity IDs are filtered (e.g. itinerary subject) but Exercise Support is
 * also selected as a course, include all Exercise Support devices on the map
 * (entity-type filter is skipped so RP/SRP still appear alongside a Student pin).
 */
function isExerciseSupportCourseCompanion(
  d: Pick<DeviceLocationStatus, 'course_id' | 'device_type' | 'entity_type'>,
  f: COPDeviceFilterSnapshot
): boolean {
  if (f.selectedEntities.length === 0 || f.entityFilterMode !== 'include') return false;
  if (f.courseFilterMode !== 'include') return false;
  if (!f.selectedCourses.includes(EXERCISE_SUPPORT_COURSE_ID)) return false;
  if ((d.course_id || '') !== EXERCISE_SUPPORT_COURSE_ID) return false;
  if (!matchesSelectedList(d.device_type || '', f.selectedDeviceTypes, f.deviceTypeFilterMode)) return false;
  return true;
}

export function deviceMatchesCOPFilters(
  d: Pick<DeviceLocationStatus, 'course_id' | 'device_type' | 'entity_type' | 'entity_id'>,
  f: COPDeviceFilterSnapshot
): boolean {
  if (isExerciseSupportCourseCompanion(d, f)) return true;

  const rpSrpCompanion = isExplicitRpSrpCompanion(d, f);
  if (!matchesSelectedList(d.course_id || '', f.selectedCourses, f.courseFilterMode) && !rpSrpCompanion) {
    return false;
  }
  if (!matchesSelectedList(d.device_type || '', f.selectedDeviceTypes, f.deviceTypeFilterMode)) return false;
  if (!matchesSelectedList(d.entity_type || '', f.selectedEntityTypes, f.entityTypeFilterMode) && !rpSrpCompanion) {
    return false;
  }
  if (!matchesSelectedList(d.entity_id || '', f.selectedEntities, f.entityFilterMode)) return false;
  return true;
}

/** Mirrors copDataService.selectedDateToDays so SSE and REST use the same windows. */
function selectedDateToDays(selectedDate: string): number | null {
  switch (selectedDate) {
    case 'Last Hour':
      return 0.04;
    case 'Last 8 Hours':
      return 0.33;
    case 'Last 12 Hours':
      return 0.5;
    case 'Last 24 Hours':
      return 1;
    case 'Last 48 Hours':
      return 2;
    case 'Last 72 Hours':
      return 3;
    case 'Last Week':
      return 7;
    case 'All Time':
      return null;
    default:
      return null;
  }
}

export function selectedDateToMaxMinutesAgo(selectedDate: string | null | undefined): number | null {
  if (!selectedDate) return null;
  const days = selectedDateToDays(selectedDate);
  if (days === null) return null;
  return Math.floor(days * 24 * 60);
}

function minutesAgoFromTimestamp(timestamp: string | undefined): number {
  if (!timestamp) return 999;
  const normalizedTs = timestamp.replace(' ', 'T');
  const time = new Date(normalizedTs).getTime();
  if (isNaN(time)) return 999;
  return Math.max(0, Math.floor((Date.now() - time) / 60000));
}

function deviceTimestampMs(timestamp: string | undefined): number | null {
  if (!timestamp) return null;
  const normalized = timestamp.replace(' ', 'T');
  const time = new Date(normalized).getTime();
  return isNaN(time) ? null : time;
}

/** Whether a device falls inside the COP top-bar time window (SSE client-side filter). */
export function deviceMatchesTimeWindow(
  d: Pick<DeviceLocationStatus, 'last_seen_minutes_ago' | 'timestamp'>,
  selectedDate: string | null | undefined,
  timeZone?: string
): boolean {
  if (!selectedDate || selectedDate === 'All Time') return true;

  if (/^\d{4}-\d{2}-\d{2}$/.test(selectedDate)) {
    const ts = deviceTimestampMs(d.timestamp);
    if (ts === null) return false;
    if (timeZone) {
      const bounds = ymdInTimeZoneToUtcInterval(selectedDate, timeZone);
      if (bounds) {
        const start = new Date(bounds.startUtc).getTime();
        const end = new Date(bounds.endUtc).getTime();
        return ts >= start && ts <= end;
      }
    }
    const dayStart = new Date(`${selectedDate}T00:00:00Z`).getTime();
    return ts >= dayStart;
  }

  const maxMinutes = selectedDateToMaxMinutesAgo(selectedDate);
  if (maxMinutes === null) return true;

  const minutesAgo = d.last_seen_minutes_ago ?? minutesAgoFromTimestamp(d.timestamp);
  return minutesAgo <= maxMinutes;
}

export function deviceMatchesCOPFiltersAndTimeWindow(
  d: Pick<
    DeviceLocationStatus,
    'course_id' | 'device_type' | 'entity_type' | 'entity_id' | 'last_seen_minutes_ago' | 'timestamp'
  >,
  filters: COPDeviceFilterSnapshot,
  selectedDate: string | null | undefined,
  timeZone?: string
): boolean {
  if (!deviceMatchesTimeWindow(d, selectedDate, timeZone)) return false;
  return deviceMatchesCOPFilters(d, filters);
}

export type COPFilterDropdownOptions = {
  deviceTypes: string[];
  entityTypes: string[];
  entities: string[];
};

/** Cascade course → device type → entity type → entity for filter dropdowns. */
export function computeCOPFilterDropdownOptions(
  devices: Pick<DeviceLocationStatus, 'course_id' | 'device_type' | 'entity_type' | 'entity_id'>[],
  filters: COPDeviceFilterSnapshot
): COPFilterDropdownOptions {
  const courseOnly: COPDeviceFilterSnapshot = {
    ...filters,
    selectedDeviceTypes: [],
    selectedEntityTypes: [],
    selectedEntities: [],
  };

  const courseFilteredDevices = filters.selectedCourses.length > 0
    ? devices.filter((d) => deviceMatchesCOPFilters(d, courseOnly))
    : devices;

  const deviceTypes = Array.from(
    new Set(courseFilteredDevices.map((d) => d.device_type).filter((t): t is string => !!t))
  ).sort();

  const entityTypes = Array.from(
    new Set(courseFilteredDevices.map((d) => d.entity_type).filter((t): t is string => !!t))
  ).sort();

  let devicesForEntityList = courseFilteredDevices;
  if (filters.selectedDeviceTypes.length > 0) {
    devicesForEntityList = devicesForEntityList.filter((d) =>
      deviceMatchesCOPFilters(d, {
        ...filters,
        selectedEntityTypes: [],
        selectedEntities: [],
      })
    );
  }
  if (filters.selectedEntityTypes.length > 0) {
    devicesForEntityList = devicesForEntityList.filter((d) =>
      deviceMatchesCOPFilters(d, {
        ...filters,
        selectedEntities: [],
      })
    );
  }

  const courseScopedEntities = devicesForEntityList
    .map((item) => item.entity_id)
    .filter((id): id is string => id !== undefined && id !== null);

  // Always offer RP/SRP from every course so companions can be multi-selected
  // after an itinerary subject is chosen, without first adding Exercise Support.
  const crossCourseRpSrp = devices
    .filter((d) => isRpOrSrpEntityType(d.entity_type))
    .map((d) => d.entity_id)
    .filter((id): id is string => id !== undefined && id !== null);

  const entities = Array.from(new Set([...courseScopedEntities, ...crossCourseRpSrp])).sort();

  return { deviceTypes, entityTypes, entities };
}

export function sortedArraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
}
